import React from 'react'
import posthog from 'posthog-js'
import i18n from '@renderer/i18n'
import { guardLanguage } from '@renderer/utils/guardLanguage'
import { crashSummaryLine, describeCrash, type CrashReport } from '@renderer/utils/crashReport'

/**
 * The last line of defence around the whole renderer tree.
 *
 * Without it, React unmounts the tree on any uncaught render/lifecycle error
 * and leaves `#root` empty — on a kiosk in fullscreen that is a plain white
 * screen with no way back, which is how B28 ("kok muncul putih putih ya") and
 * B9 ("aplikasi tidak berfungsi di tablet") reach us: no message, no error
 * code, and the operator can only reach for the power button.
 *
 * This does not fix whatever threw. It makes the crash *legible* — a titled
 * screen, the error line to read out to support, and a reload button — and
 * reportable, via `console.error` (which lands in the booth log file) and a
 * `renderer_crash` PostHog event.
 */
interface RootErrorBoundaryState {
  report: CrashReport | null
}

export default class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { report: null }

  static getDerivedStateFromError(error: unknown): RootErrorBoundaryState {
    return { report: describeCrash(error) }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    const report = describeCrash(error, info?.componentStack)
    // Keep the richer report (it has the component stack) over the one
    // getDerivedStateFromError already put on state.
    this.setState({ report })

    // Goes to the booth's log file, so a crash is diagnosable from logs alone.
    console.error('[RENDERER-CRASH]', crashSummaryLine(report), {
      stack: report.stack,
      componentStack: report.componentStack
    })

    try {
      posthog.capture('renderer_crash', {
        error_name: report.name,
        error_message: report.message,
        component_stack: report.componentStack
      })
    } catch {
      // Telemetry must never be the reason the crash screen fails to render.
    }
  }

  render(): React.ReactNode {
    const { report } = this.state
    if (!report) return this.props.children
    return <CrashScreen report={report} />
  }
}

/**
 * Rendered with a fixed `t` rather than `useTranslation()`: the tree this sits
 * above has just thrown, so the i18n React context may be gone, and the crash
 * may well *be* an i18n failure. Same approach as `DuplicateLaunchGate`.
 */
const CrashScreen: React.FC<{ report: CrashReport }> = ({ report }) => {
  const t = i18n.getFixedT(guardLanguage())

  return (
    <div className="bg-whiteout flex h-screen items-center justify-center p-6">
      <div className="w-full max-w-lg p-8 bg-white rounded-lg shadow-2xl text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('crash.title')}</h1>
        <p className="text-gray-600 mb-6">{t('crash.body')}</p>
        <button
          onClick={(): void => {
            posthog.capture('renderer_crash_reloaded', { error_name: report.name })
            window.location.reload()
          }}
          className="w-full py-3 px-6 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 focus:ring-4 focus:ring-purple-300 transition duration-300"
        >
          {t('crash.reload')}
        </button>
        <p className="text-sm text-gray-600 mt-6">{t('crash.contact')}</p>
        <p className="text-xs text-gray-500 mt-2 font-mono break-words" data-testid="crash-summary">
          {crashSummaryLine(report)}
        </p>
      </div>
    </div>
  )
}
